import { CommonModule } from "@angular/common";
import { Component, Input, OnChanges, OnInit, SimpleChanges, TemplateRef } from "@angular/core";
import {
  AbstractControl,
  AsyncValidatorFn,
  FormArray,
  FormControl,
  FormGroup,
  ValidationErrors,
  ValidatorFn,
} from "@angular/forms";
import { Observable } from "rxjs";
import { SelectComponent } from "../../../form/components/select/select.component";
import { FormModel, IdObjectFormModel } from "../../../form/form-models/form-model";
import { BasicDto, IdObject } from "../../../models/basicDto";
import { SelectOption } from "../../../models/option";
import { ActionGroupComponent } from "../action-group/action-group.component";
import { DefaultActionsGroupComponent } from "../default-actions-group/default-actions-group.component";
import { TableAction } from "../sortable-table/sortable-table.component";
import { ComponentColumn, TableColumn, TableConfig } from "../table-config";
import { TableGroupComponent } from "./table-group/table-group.component";
import { TableRowComponent } from "./table-row/table-row.component";

/*export interface MultiLevelItem<T> extends BasicDto<number> {
  Children: this[] | T[];
  collapse: boolean;
}

export interface MultiLevelItemFormModel<FM extends FormModel<any>>
  extends FormModel<MultiLevelItem<FM>> {
  Id: FormControl<number>;
  Children: FormArray<this> | FormArray<FM>;
  collapse: FormControl<boolean>;
}*/

export interface MultiI {
  Children: any;
}

export interface MultiItem<L extends IdObject<number> = never> extends IdObject<number>, MultiI {
  Children: this[] | L[];
  collapse: boolean;
}

export interface MultiItemWithName<L extends IdObject<number> = never>
  extends MultiItem<L>,
    BasicDto<number> {}

export abstract class MultiItemFormModel<
    MI extends MultiItem<L>,
    L extends IdObject<number> = never,
    LFM extends FormModel<L> = never,
  >
  extends IdObjectFormModel<MI>
  implements MultiI
{
  constructor(model: MI, formGroup: FormGroup) {
    formGroup.addControl("collapse", new FormControl(model.collapse));
    super(model, formGroup);
  }

  abstract Children: FormArray; // = this.get("Children") as FormArray;
  collapse: FormControl<boolean> = this.get("collapse") as FormControl<boolean>;
}

export interface MultiWrapper<A, B> {
  get Id(): number;
  get Children(): (A | B)[] | null;
  get wrapperChildren(): MultiWrapper<A, B>[] | null;
  setChild(index: number, child: A | B): void;
  removeChild(index: number): void;
  addChild(child: A | B): void;
  hasChildren(instance: any): instance is A;
  hasNoChildren(instance: any): instance is B;
  get collapse(): boolean | null;
  set collapse(collapse: boolean);

  get isSelected(): boolean;
  setSelected(selected: boolean, onlySelf?: boolean): void;

  get instance(): A | B;
}

export class MultiItemOrItemWrapper<MI extends MultiItem<L>, L extends IdObject<number> = never>
  implements MultiWrapper<MI, L>
{
  private _instance: MI | L;

  constructor(instance: MI | L) {
    this._instance = instance;
  }

  get Id() {
    return this._instance.Id;
  }

  get Children(): (MI | L)[] | null {
    if ("Children" in this._instance) {
      return this._instance.Children;
    }
    return null;
  }

  get collapse(): boolean | null {
    if ("Children" in this._instance) {
      return this._instance.collapse;
    }
    return null;
  }

  set collapse(collapse: boolean) {
    if ("Children" in this._instance) {
      this._instance.collapse = collapse;
    }
  }

  get instance() {
    return this._instance;
  }
  hasChildren(instance: any): instance is MI {
    return "Children" in this._instance;
  }

  hasNoChildren(instance: any): instance is L {
    return !this.hasChildren(instance);
  }

  get isSelected() {
    return this.instance.isSelected ?? false;
  }

  setSelected(selected: boolean, onlySelf = false) {
    this._instance.isSelected = selected;
    if (!onlySelf) {
      this.wrapperChildren?.forEach((child) => {
        child.setSelected(selected);
      });
    }
  }

  get wrapperChildren(): MultiItemOrItemWrapper<MI, L>[] | null {
    return this.Children?.map((child) => new MultiItemOrItemWrapper<MI, L>(child)) ?? null;
  }

  setChild(index: number, child: MI | L) {
    if (this.Children == null) {
      throw new Error("Can't set children for leaf node");
    }
    this.Children[index] = child;
  }

  removeChild(index: number) {
    if (this.Children == null) {
      throw new Error("Can't remove children for leaf node");
    }
    this.Children.splice(index, 1);
  }

  addChild(child: MI | L) {
    if (this.Children == null) {
      throw new Error("Can't add children for leaf node");
    }
    this.Children.push(child);
  }
}

export class MIFormModelorFormModelWrapper<
    MI extends MultiItemFormModel<MultiItem<L>, L, LFM>,
    L extends IdObject<number>,
    LFM extends IdObjectFormModel<L>,
  >
  extends AbstractControl
  implements MultiWrapper<MI, LFM>
{
  private _instance: MI | LFM;
  constructor(instance: MI | LFM) {
    super(null, null);
    this._instance = instance;
  }
  /*private _parent = null;
  private _asyncValidationSubscription = null;
  private _updateAncestors = null;
  private _setInitialStatus = null;
  private _runValidator = null;
  private _runAsyncValidator = null;
  private _cancelExistingSubscription = null;
  private _calculateStatus = null;
  private _assignValidators = null;
  private _assignAsyncValidators = null;*/

  get Id() {
    return this._instance.Id.value;
  }

  get Children() {
    if ("Children" in this._instance) {
      return this._instance.Children.controls as MI[] | LFM[];
    }
    return null;
  }

  get collapse(): boolean | null {
    if ("Children" in this._instance) {
      return this._instance.collapse.value;
    }
    return null;
  }

  set collapse(collapse: boolean) {
    if ("Children" in this._instance) {
      this._instance.collapse.setValue(collapse);
    }
  }

  get instance() {
    return this._instance;
  }

  get isSelected() {
    return this.instance.isSelected.value ?? false;
  }

  setSelected(selected: boolean, olnySelf = false) {
    this.instance.isSelected.patchValue(selected);
    if (!olnySelf) {
      this.wrapperChildren?.forEach((child) => child.setSelected(selected));
    }
  }
  get wrapperChildren(): MIFormModelorFormModelWrapper<MI, L, LFM>[] | null {
    return (
      this.Children?.map((child) => new MIFormModelorFormModelWrapper<MI, L, LFM>(child)) ?? null
    );
  }

  hasChildren(instance: any): instance is MI {
    return "Children" in this._instance;
  }

  hasNoChildren(instance: any): instance is LFM {
    return !this.hasChildren(instance);
  }

  setChild(index: number, child: MI | LFM) {
    if (!("Children" in this._instance)) {
      throw new Error("Can't set children for leaf node");
    }
    this._instance.Children.setControl(index, child);
  }

  removeChild(index: number) {
    if (!("Children" in this._instance)) {
      throw new Error("Can't remove children for leaf node");
    }
    this._instance.Children.removeAt(index);
  }

  addChild(child: MI | LFM) {
    if (!("Children" in this._instance)) {
      throw new Error("Can't add children for leaf node");
    }
    this._instance.Children.push(child);
  }

  override get validator(): ValidatorFn | null {
    return this._instance.validator;
  }
  override set validator(validatorFn: ValidatorFn | null) {
    this._instance.validator = validatorFn;
  }
  override get asyncValidator(): AsyncValidatorFn | null {
    return this._instance.asyncValidator;
  }
  override set asyncValidator(asyncValidatorFn: AsyncValidatorFn | null) {
    this._instance.asyncValidator = asyncValidatorFn;
  }
  override get parent(): FormGroup<any> | FormArray<any> | null {
    return this._instance.parent;
  }
  //override status: FormControlStatus = this._instance.status;
  override get valid(): boolean {
    return this._instance.valid;
  }
  override get invalid(): boolean {
    return this._instance.invalid;
  }
  override get pending(): boolean {
    return this._instance.pending;
  }
  override get disabled(): boolean {
    return this._instance.disabled;
  }
  override get enabled(): boolean {
    return this._instance.enabled;
  }
  override get dirty(): boolean {
    return this._instance.dirty;
  }
  override get untouched(): boolean {
    return this._instance.untouched;
  }
  override get updateOn(): "change" | "blur" | "submit" {
    return this._instance.updateOn;
  }
  override setValidators(validators: ValidatorFn | ValidatorFn[] | null): void {
    this._instance.setValidators(validators);
  }
  override setAsyncValidators(validators: AsyncValidatorFn | AsyncValidatorFn[] | null): void {
    this._instance.setAsyncValidators(validators);
  }
  override addValidators(validators: ValidatorFn | ValidatorFn[]): void {
    this._instance.addValidators(validators);
  }
  override addAsyncValidators(validators: AsyncValidatorFn | AsyncValidatorFn[]): void {
    this._instance.addAsyncValidators(validators);
  }
  override removeValidators(validators: ValidatorFn | ValidatorFn[]): void {
    this._instance.removeValidators(validators);
  }
  override removeAsyncValidators(validators: AsyncValidatorFn | AsyncValidatorFn[]): void {
    this._instance.removeAsyncValidators(validators);
  }
  override hasValidator(validator: ValidatorFn): boolean {
    return this._instance.hasValidator(validator);
  }
  override hasAsyncValidator(validator: AsyncValidatorFn): boolean {
    return this._instance.hasAsyncValidator(validator);
  }
  override clearValidators(): void {
    this._instance.clearValidators();
  }
  override clearAsyncValidators(): void {
    this._instance.clearAsyncValidators();
  }
  override markAsTouched(opts?: { onlySelf?: boolean }): void {
    this._instance.markAsTouched(opts);
  }
  override markAllAsTouched(): void {
    this._instance.markAllAsTouched();
  }
  override markAsUntouched(opts?: { onlySelf?: boolean }): void {
    this._instance.markAsUntouched(opts);
  }
  override markAsDirty(opts?: { onlySelf?: boolean }): void {
    this._instance.markAsDirty(opts);
  }
  override markAsPristine(opts?: { onlySelf?: boolean }): void {
    this._instance.markAsPristine(opts);
  }
  override markAsPending(opts?: { onlySelf?: boolean; emitEvent?: boolean }): void {
    this._instance.markAsPending(opts);
  }
  override disable(opts?: { onlySelf?: boolean; emitEvent?: boolean }): void {
    this._instance.disable(opts);
  }
  override enable(opts?: { onlySelf?: boolean; emitEvent?: boolean }): void {
    this._instance.enable(opts);
  }
  override setParent(parent: FormGroup | FormArray | null): void {
    this._instance.setParent(parent);
  }
  override setValue(value: any, options?: Object): void {
    this._instance.setValue(value, options);
  }
  override patchValue(value: any, options?: Object): void {
    this._instance.patchValue(value, options);
  }
  override reset(value?: any, options?: Object): void {
    this._instance.reset(value, options);
  }
  override getRawValue() {
    this._instance.getRawValue();
  }
  override updateValueAndValidity(opts?: { onlySelf?: boolean; emitEvent?: boolean }): void {
    this._instance.updateValueAndValidity(opts);
  }
  override setErrors(errors: ValidationErrors | null, opts?: { emitEvent?: boolean }): void {
    this._instance.setErrors(errors, opts);
  }
  override get(
    path: unknown
  ):
    | AbstractControl<
        import("@angular/forms").ɵGetProperty<any, any>,
        import("@angular/forms").ɵGetProperty<any, any>
      >
    | AbstractControl<
        import("@angular/forms").ɵGetProperty<any, any>,
        import("@angular/forms").ɵGetProperty<any, any>
      >
    | null {
    throw new Error("Method not implemented.");
  }
  override getError(errorCode: string, path?: Array<string | number> | string) {
    return this._instance.getError(errorCode, path);
  }
  override hasError(errorCode: string, path?: Array<string | number> | string): boolean {
    return this._instance.hasError(errorCode, path);
  }
  override get root(): AbstractControl<any, any> {
    return this._instance.root;
  }
}

/*export class MIWrapperWrapper<
  MI extends MultiItem<L> | MultiItemFormModel<MultiItem<L>, L, LFM>,
  L extends IdObject<number> = never,
  LFM extends IdObjectFormModel<L> = never,
> {
  private _instance: MI extends MultiItem<L>
    ? MultiItemOrItemWrapper<MI, L>
    : MI extends MultiItemFormModel<MultiItem<L>, L, LFM>
      ? MIFormModelorFormModelWrapper<MI, L, LFM>
      : never;

  constructor(instance: MI) {
    if (instance instanceof MultiItem<L>) {
      this._instance = new MultiItemOrItemWrapper(instance);
    }
  }

  getChildren(): L[] | LFM[] | null {
    return this._instance.Children;
  }
  get Children(): MIWrapper<MI, L, LFM>[] | L[] | LFM[] {
    let children: MI[] | L[] | LFM[] = this.getChildren();
    if (this.isMI(children)) {
      return children.map((child) => new MIWrapper(child));
    }
    return children;
  }

  isMI(children: MI[] | L[] | LFM[]): children is MI[] {
    return children.length > 0 && children[0] instanceof Object && "Children" in children[0];
  }

  get collapse(): boolean {
    if (this._instance.collapse instanceof FormControl) {
      return this._instance.collapse.value;
    }
    return this._instance.collapse;
  }

  set collapse(newCollapse: boolean) {
    if (this._instance.collapse instanceof FormControl) {
      this._instance.collapse.setValue(newCollapse);
    } else {
      this._instance.collapse = newCollapse;
    }
  }

  get instance(): MI {
    return this._instance;
  }

  get Id() {
    if (this._instance.Id instanceof FormControl) {
      return this._instance.Id.value;
    }
    return this._instance.Id;
  }

  removeChild(child: MI | L | LFM) {
    if (this._instance.Children == undefined || this._instance.Children.length === 0) {
      throw new Error("Impossible to delete in an empty itemlist");
    }
    let wrapperChildren = this.Children;
    let itemIndex = 0;
    while (
      itemIndex < this._instance.Children.length &&
      wrapperChildren[itemIndex].Id !=
        ("Children" in child ? new MIWrapper<MI, L>(child) : child).Id
    ) {
      itemIndex++;
    }
    if (this._instance.Children instanceof FormArray) {
      this._instance.Children.removeAt(itemIndex);
    } else {
      this._instance.Children.splice(itemIndex, 1);
    }
  }

  /**
   * TODO: ideally should not use MoveAction but two function inputs
   */
/*moveChild(action: MoveAction<MI | L | LFM>, child: MI | L | LFM, direction: number) {
    if (this.getChildren() == undefined && this.getChildren().length == 0) {
      throw new Error("Impossible to move for empty items");
    }

    let wrapperChildren = this.Children;
    let itemIndex = 0;

    while (
      itemIndex < wrapperChildren.length &&
      wrapperChildren[itemIndex].Id !=
        ("Children" in child ? new MIWrapper<MI, L>(child) : child).Id
    ) {
      itemIndex++;
    }
    let otherIndex = itemIndex + direction;

    let firstItem = this.getChildren()[itemIndex];
    let secondItem = this.getChildren()[otherIndex];

    let firstOrder = action.getOrder(firstItem);
    let secondOrder = action.getOrder(secondItem);

    action.setOrder(firstItem, secondOrder);
    action.setOrder(secondItem, firstOrder);

    if (this._instance.Children instanceof FormArray) {
      this._instance.Children.setControl(itemIndex, secondItem);
      this._instance.Children.setControl(otherIndex, firstItem);
    } else {
      let otherItem = this._instance.Children[otherIndex];
      this._instance.Children[otherIndex] = this._instance.Children[itemIndex];
      this._instance.Children[itemIndex] = otherItem;
    }
  }
}

export class MIWrapper<
  MI extends MultiItem<L> | MultiItemFormModel<MultiItem<L>, L, LFM>,
  L extends IdObject<number> = never,
  LFM extends IdObjectFormModel<L> = never,
> {
  private _instance: MI;

  constructor(instance: MI) {
    this._instance = instance;
  }

  getChildren(): MI[] | L[] | LFM[] {
    if (this._instance.Children instanceof FormArray) {
      return this._instance.Children.controls as MI[] | LFM[];
    }
    return this._instance.Children as MI[] | L[];
  }
  get Children(): MIWrapper<MI, L, LFM>[] | L[] | LFM[] {
    let children: MI[] | L[] | LFM[] = this.getChildren();
    if (this.isMI(children)) {
      return children.map((child) => new MIWrapper(child));
    }
    return children;
  }

  isMI(children: MI[] | L[] | LFM[]): children is MI[] {
    return children.length > 0 && children[0] instanceof Object && "Children" in children[0];
  }

  get collapse(): boolean {
    if (this._instance.collapse instanceof FormControl) {
      return this._instance.collapse.value;
    }
    return this._instance.collapse;
  }

  set collapse(newCollapse: boolean) {
    if (this._instance.collapse instanceof FormControl) {
      this._instance.collapse.setValue(newCollapse);
    } else {
      this._instance.collapse = newCollapse;
    }
  }

  get instance(): MI {
    return this._instance;
  }

  get Id() {
    return this.getId(this._instance);
    /*if (this._instance.Id instanceof FormControl) {
      return this._instance.Id.value;
    }
    return this._instance.Id;*/
/*
  }

  getId(object: IdObject<number> | IdObjectFormModel<IdObject<number>>): number {
    if (object.Id instanceof FormControl) {
      return object.Id.value;
    }
    return object.Id;
  }

  removeChild(child: MI | L | LFM) {
    if (this._instance.Children == undefined || this._instance.Children.length === 0) {
      throw new Error("Impossible to delete in an empty itemlist");
    }
    let wrapperChildren = this.Children;
    let itemIndex = 0;
    while (
      itemIndex < this._instance.Children.length &&
      this.getId(wrapperChildren[itemIndex]) != this.getId(child)
    ) {
      itemIndex++;
    }
    if (this._instance.Children instanceof FormArray) {
      this._instance.Children.removeAt(itemIndex);
    } else {
      this._instance.Children.splice(itemIndex, 1);
    }
  }

  /**
   * TODO: ideally should not use MoveAction but two function inputs
   */
/*moveChild(action: MoveAction<MI | L | LFM>, child: MI | L | LFM, direction: number) {
    if (this.getChildren() == undefined && this.getChildren().length == 0) {
      throw new Error("Impossible to move for empty items");
    }

    let wrapperChildren = this.Children;
    let itemIndex = 0;

    while (
      itemIndex < wrapperChildren.length &&
      wrapperChildren[itemIndex].Id !=
        ("Children" in child ? new MIWrapper<MI, L, LFM>(child) : child).Id
    ) {
      itemIndex++;
    }
    let otherIndex = itemIndex + direction;

    let firstItem = this.getChildren()[itemIndex];
    let secondItem = this.getChildren()[otherIndex];

    let firstOrder = action.getOrder(firstItem);
    let secondOrder = action.getOrder(secondItem);

    action.setOrder(firstItem, secondOrder);
    action.setOrder(secondItem, firstOrder);

    if (this._instance.Children instanceof FormArray) {
      if (firstItem && secondItem instanceof FormModel) {
        this._instance.Children.setControl(itemIndex, secondItem);
        this._instance.Children.setControl(otherIndex, firstItem);
      } else {
        secondItem;
      }
    } else {
      let otherItem = this._instance.Children[otherIndex];
      this._instance.Children[otherIndex] = this._instance.Children[itemIndex];
      this._instance.Children[itemIndex] = otherItem;
    }
  }
}

export interface MITest extends MultiItem<BasicDto<number>> {
  prop: number;
}

export class MIFTest extends MultiItemFormModel<
  MITest,
  BasicDto<number>,
  FormModel<BasicDto<number>>
> {
  constructor(
    model: MITest,
    fb: FormBuilder = new FormBuilder()
  ) {
    super(
      model,
      fb.group({
        Children: new FormArray(
          model.Children.map((child) =>
            "Children" in child ? new MIFTest(child) : new FormModel(child, fb.group({}).controls)
          )
        ),
      })
    );
  }

  readonly Children: FormArray<this> | FormArray<FormModel<IdObject<number>>> = this.get(
    "Children"
  ) as FormArray<this> | FormArray<FormModel<IdObject<number>>>;

  createWrapper() {
    let what = new MIFTest({} as MITest);
    what.Children;
    let wrapper = new MIWrapper<MIFTest, BasicDto<number>, FormModel<BasicDto<number>>>(what);
    wrapper.getChildren();
  }
}

/*export class MultiLevelItemWrapper<
  MI extends MultiLevelItem<any>,
  MIFM extends MultiLevelItemFormModel<any>,
> {
  private multilevelItem?: MI;
  private multilevelFormModel?: MIFM;

  constructor(item: MI);
  constructor(itemFM: MIFM);
  constructor(item?: MI, itemFM?: MIFM) {
    if (item) {
      this.multilevelItem = item;
    } else if (itemFM) {
      this.multilevelFormModel = itemFM;
    } else {
      throw new Error("item is required");
    }
  }

  setMultiLevelItem(item: MI) {
    let wrapper = new MultiLevelItemWrapper(item);
    this.multilevelItem = item;
    this.multilevelFormModel = undefined;
  }

  setMultiLevelFormModel(item: MIFM) {
    let wrapper = new MultiLevelItemWrapper(item);

    this.multilevelFormModel = item;
    this.multilevelItem = undefined;
  }

  static fromMultiLevelitem<
    T extends Object,
    MI extends MultiLevelItem<T>,
    FM extends FormModel<T>,
    MIFM extends MultiLevelItemFormModel<FM>,
  >(item: MI): MultiLevelItemWrapper<MI, MIFM> {
    let wrapper = new MultiLevelItemWrapper<MI, MIFM>(item);
    wrapper.setMultiLevelItem(item);
    return wrapper;
  }

  static fromMultiLevelFormModel<
    T extends Object,
    MI extends MultiLevelItem<T>,
    FM extends FormModel<T>,
    MIFM extends MultiLevelItemFormModel<FM>,
  >(item: MIFM): MultiLevelItemWrapper<MI, MIFM> {
    let wrapper = new MultiLevelItemWrapper<MI, MIFM>(item);
    wrapper.setMultiLevelFormModel(item);
    return wrapper;
  }

  get Children(): MI[] | any[] | MIFM[] {
    if (this.multilevelItem != undefined) {
      return this.multilevelItem.Children;
    }
    return this.multilevelFormModel!.Children.typedControls();
  }

  get item(): MI | any | MIFM {
    if (this.multilevelItem != undefined) {
      return this.multilevelItem;
    }
    return this.multilevelFormModel!;
  }
}*/

type ExtractLeafType<MI, L extends IdObject<number>, LFM> = MI extends MultiItem<L> ? L : LFM;
export interface MultiLevelTableConfig<
  MI extends (MultiItem<L> & {}) | MultiItemFormModel<MultiItem<L>, L, LFM>,
  L extends IdObject<number> = never,
  LFM extends IdObjectFormModel<any> = never,
> extends TableConfig<MI | ExtractLeafType<MI, L, LFM>> {
  itemLabel: string;
  customActions: TableAction<MI | ExtractLeafType<MI, L, LFM>>[];
  actions?: DefaultTableAction<MI | ExtractLeafType<MI, L, LFM>>[];
  /**
   * @deprecated todo: remove
   */
  getId: (item: MI) => any;
  getDisplayName: (item: MI | ExtractLeafType<MI, L, LFM>) => string;
  columns: MultiLevelTableColumn<MI, ExtractLeafType<MI, L, LFM>>[];
}

export interface MultiLevelTableColumn<A extends MultiI, B> extends TableColumn<A | B> {
  formatA?: (row: A) => string;
  formatB?: (row: B) => string;
  templateA?: TemplateRef<any>;
  templateB?: TemplateRef<any>;
  componentA?: ComponentColumn<A>;
  componentB?: ComponentColumn<B>;
  colSpanA?: number;
  colSpanB?: number;
  colSpanTh?: number;
}

export type DefaultTableAction<T> =
  | DeleteAction<T>
  | MoveAction<T>
  | PredefinedAddAction<T>
  | FullAddAction<T>
  | EditAction<T>;
export interface DeleteAction<T> {
  kind: "delete";
  canDelete: (item: T) => "yes" | "hide" | "disabled";
  deleteItem: (item: T) => Observable<Object>;
  onDisabledClick?: (item: T) => void;
}

export interface MoveAction<T> {
  kind: "move";
  canMove: (item: T) => boolean;
  getOrder: (item: T) => number;
  setOrder: (item: T, newOrder: number) => void;
}

export interface AddAction<T> {
  kind: "add";
  canAdd: (item: T) => boolean;
  getOrder?: (item: T) => number;
  setOrder?: (item: T, newOrder: number) => void;
}

export interface PredefinedAddAction<T> extends AddAction<T> {
  addType: "predefined";
  getPossibleItemsToAdd: (item: T) => Observable<SelectOption[]>;
}

export interface FullAddAction<T> extends AddAction<T> {
  addType: "full";
  saveItem: (item: T) => Observable<T>;
}

export interface EditAction<T> {
  kind: "edit";
  canEdit: (item: T) => "simple" | "full" | "no";
  editItem: (item: T) => void;
}
/**
 * @deprecated use a null check?
 */ /*
export function isMultiLevelItem<T, M extends MultiLevelItem<T>>(item: M | T): item is M {
  return (item as M).Children !== undefined;
}*/

export interface ItemActionCombo<T> {
  action: MoveAction<T>;
  item: T;
}
@Component({
  selector: "app-multi-level-table",
  standalone: true,
  imports: [
    ActionGroupComponent,
    TableRowComponent,
    DefaultActionsGroupComponent,
    SelectComponent,
    TableGroupComponent,
    CommonModule,
  ],
  templateUrl: "./multi-level-table.component.html",
  styleUrl: "./multi-level-table.component.scss",
})
export class MultiLevelTableComponent<
    MI extends MultiItem<L> | MultiItemFormModel<MultiItem<L>, L, LFM>,
    L extends IdObject<number> = never,
    LFM extends IdObjectFormModel<any> = never,
  >
  implements OnInit, OnChanges
{
  @Input() showCollapseButton = false;
  @Input() showIndent = false;
  @Input() forceShowAsMultiLevelItem = false;
  @Input() showHeaders = false;
  @Input() enableSelect: boolean = false;
  @Input() disableSelectButtons: boolean = false;
  @Input()
  config!: MultiLevelTableConfig<MI, L, LFM>;
  @Input() items: MultiWrapper<MI, ExtractLeafType<MI, L, LFM>>[] = [];

  @Input() itemWrapperConstructor?: new (
    item: MI | (MI extends MultiItem<L> ? L : LFM)
  ) => MultiWrapper<MI, ExtractLeafType<MI, L, LFM>>;

  @Input() colorDepth?: number;

  protected addFormModel = new FormControl();
  protected showAddInput = false;
  protected addOptions = [];
  protected itemDepth = 0;

  ngOnInit(): void {
    this.itemDepth = calculateDepth(this.items);
  }

  ngOnChanges(changes: SimpleChanges): void {
    // this.items is hier op een of andere manier nog niet geupdate
    setTimeout(() => this.ngOnInit(), 250);
  }
  containsChildren(): boolean {
    return this.items.some((item) => item.Children != null && item.Children.length > 0);
    /*if (this.isMultiLevelItemArray(items)) {
      return items.some((item) => (item.Children ?? []).length > 0);
    }
    return false;*/
  }

  hasFooter(): boolean {
    return this.config.columns.some((col) => col.footer != undefined);
  }
  /*
  isMultiLevelItemArray(items: MI[] | T[], returnTrueWhenEmpty = true): items is MI[] {
    if (!returnTrueWhenEmpty && items.length == 0) {
      return false;
    }
    return items.every((item) => isMultiLevelItem<T, MI>(item));
  }*/

  toggleCollapse(group: MultiWrapper<MI, MI extends MultiItem ? L : LFM>) {
    group.collapse = !group.collapse;
  }

  moveItem(
    action: MoveAction<MI | (MI extends MultiItem<L> ? L : LFM)>,
    item: MI | (MI extends MultiItem<L> ? L : LFM),
    index: number,
    direction: number
  ) {
    if (this.items == undefined) {
      throw new Error("Impossible to move for empty items");
    }
    let otherIndex = index + direction;
    let otherItem = this.items[otherIndex];
    //Change item order field
    let otherOrder: number = action.getOrder(otherItem.instance);
    let itemOrder: number = action.getOrder(item);
    action.setOrder(this.items[index].instance, otherOrder);
    action.setOrder(otherItem.instance, itemOrder);

    // Change order in list
    this.items[otherIndex] = this.items[index];
    this.items[index] = otherItem;
  }

  removeItem(index: number) {
    this.items.splice(index, 1);
  }

  addSelectedToGroup() {}

  openAddModal(
    action:
      | PredefinedAddAction<MI | (MI extends MultiItem<L> ? L : LFM)>
      | FullAddAction<MI | (MI extends MultiItem<L> ? L : LFM)>
  ) {
    if (action.addType === "full") {
      action.saveItem({} as MI).subscribe((result) => {
        if (result && this.itemWrapperConstructor) {
          let order =
            this.items.length > 0
              ? action.getOrder
                ? action.getOrder(this.items[this.items.length - 1].instance)
                : 0
              : 0;
          if (action.setOrder) action.setOrder(result, order + 1);
          this.items.push(new this.itemWrapperConstructor(result));
          //TODO, should result be a wrapper here?
        } else {
          console.log("Failed", result, this.itemWrapperConstructor);
        }
      });
    } else if (action.addType == "predefined") {
      //TODO
      /*} else if (action.addType == "simple") {*/
    }
  }
  createWrapperInstance<X>(
    wrapper: new (item: X) => MultiWrapper<MI, ExtractLeafType<MI, L, LFM>>,
    item: X
  ): MultiWrapper<MI, ExtractLeafType<MI, L, LFM>> {
    return new wrapper(item);
  }
  addItem() {
    this.itemDepth = calculateDepth(this.items);
  }
}
export function calculateDepth(items: MultiWrapper<any, any>[] | null): number {
  if (items == undefined || items == null) {
    return 0;
  }
  if (items.length == 0) {
    return 0;
  }
  let max =
    Math.max(
      ...items.map((item) => {
        return calculateDepth(item.wrapperChildren);
      })
    ) + 1;
  return max;
}
